import React from 'react'
import styled from 'styled-components'

import { useAccordionState, useAccordionDispatch } from './AccordionProvider'

import { ChevronUp } from 'pcln-icons'
import { Flex, IconButton } from 'pcln-design-system'

const TriggerContainer = styled(Flex)`
  line-height: ${({ size }) => size}px;
  cursor: pointer;
`

const ChevronButton = styled(IconButton)`
  ${({ shouldAnimate }) => (shouldAnimate ? 'transition: transform 0.4s;' : null)}

  ${({ open }) => (open ? null : 'transform: rotate(180deg);')}
`

const StyledFlex = styled(Flex)`
  flex: 1;
`

export interface IAccordionTrigger {
  children?: React.ReactNode
  color?: string
  size?: string | number
  iconMR?: string | number
  containerPadding?: string | number
  hideIcon?: boolean
  rightIcon?: boolean
  onClose?: (e: React.SyntheticEvent<HTMLDivElement>) => void
  onOpen?: (e: React.SyntheticEvent<HTMLDivElement>) => void
}

const AccordionTrigger = ({
  children,
  size = 24,
  color = 'text',
  iconMR = 2,
  containerPadding = 2,
  hideIcon = false,
  rightIcon = false,
  onClose,
  onOpen,
  ...props
}: IAccordionTrigger) => {
  const { open, shouldAnimate } = useAccordionState()
  const dispatch = useAccordionDispatch()

  function onClick(e: React.MouseEvent<HTMLDivElement>) {
    dispatch('toggle')
    if (open && onClose) {
      onClose(e)
    } else if (!open && onOpen) {
      onOpen(e)
    }
  }

  const chevronIcon = (
    <ChevronButton
      {...props}
      mr={iconMR}
      icon={<ChevronUp size={size} color={color} />}
      open={open}
      shouldAnimate={shouldAnimate}
      aria-label='Toggle accordion state'
      aria-expanded={!!open}
    />
  )

  const showLeftIcon = !hideIcon && !rightIcon
  const showRightIcon = !hideIcon && rightIcon

  const renderChildren = showRightIcon ? <StyledFlex alignItems='center'>{children}</StyledFlex> : children

  return (
    <TriggerContainer
      data-testid='trigger-container'
      alignItems='center'
      onClick={onClick}
      p={containerPadding}
    >
      {showLeftIcon && chevronIcon}
      {renderChildren}
      {showRightIcon && chevronIcon}
    </TriggerContainer>
  )
}

export default AccordionTrigger
